import React, { createContext, useContext, useReducer, useEffect, useCallback, useRef, ReactNode, useMemo } from 'react';
import { AppState, AppAction, Note, Folder, Tag, AppSettings, defaultSettings, generateId } from '../types';
import realStorageService from '../services/RealStorageService';

// 初始状态
const initialState: AppState = {
  notes: [],
  folders: [],
  tags: [],
  currentNote: null,
  currentFolder: null,
  searchQuery: '',
  isLoading: false,
  error: null,
  settings: defaultSettings,
  currentUser: null
};

// Reducer函数
const appReducer = (state: AppState, action: AppAction): AppState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    
    case 'SET_NOTES':
      return { ...state, notes: action.payload };
    
    case 'ADD_NOTE': {
      // payload现在是完整的Note对象
      return { ...state, notes: [action.payload, ...state.notes] };
    }
    
    case 'UPDATE_NOTE': {
      const updatedNote = { ...action.payload, updatedAt: new Date() };
      const updatedNotes = state.notes.map(note => 
        note.id === updatedNote.id ? updatedNote : note
      );
      return { 
        ...state, 
        notes: updatedNotes,
        currentNote: state.currentNote?.id === updatedNote.id ? updatedNote : state.currentNote
      };
    }
    
    case 'DELETE_NOTE': {
      const filteredNotes = state.notes.filter(note => note.id !== action.payload);
      return { 
        ...state, 
        notes: filteredNotes,
        currentNote: state.currentNote?.id === action.payload ? null : state.currentNote
      };
    }
    
    case 'SET_CURRENT_NOTE':
      return { ...state, currentNote: action.payload };
    
    case 'SET_FOLDERS':
      return { ...state, folders: action.payload };
    
    case 'ADD_FOLDER': {
      const newFolder: Folder = {
        ...action.payload,
        id: generateId(),
        createdAt: new Date(),
        updatedAt: new Date()
      };
      return { ...state, folders: [...state.folders, newFolder] };
    }
    
    case 'UPDATE_FOLDER': {
      const updatedFolder = { ...action.payload, updatedAt: new Date() };
      const updatedFolders = state.folders.map(folder => 
        folder.id === updatedFolder.id ? updatedFolder : folder
      );
      return { ...state, folders: updatedFolders };
    }
    
    case 'DELETE_FOLDER': {
      const filteredFolders = state.folders.filter(folder => folder.id !== action.payload);
      // 将被删除文件夹中的笔记移动到默认文件夹
      const updatedNotes = state.notes.map(note => 
        note.folderId === action.payload ? { ...note, folderId: 'default' } : note
      );
      return { 
        ...state, 
        folders: filteredFolders,
        notes: updatedNotes,
        currentFolder: state.currentFolder === action.payload ? null : state.currentFolder
      };
    }
    
    case 'SET_CURRENT_FOLDER':
      return { ...state, currentFolder: action.payload };
    
    case 'SET_TAGS':
      return { ...state, tags: action.payload };
    
    case 'ADD_TAG': {
      const newTag: Tag = {
        ...action.payload,
        id: generateId()
      };
      return { ...state, tags: [...state.tags, newTag] };
    }
    
    case 'UPDATE_TAG': {
      const updatedTags = state.tags.map(tag => 
        tag.id === action.payload.id ? action.payload : tag
      );
      return { ...state, tags: updatedTags };
    }
    
    case 'DELETE_TAG': {
      const filteredTags = state.tags.filter(tag => tag.id !== action.payload);
      // 从所有笔记中移除该标签
      const updatedNotes = state.notes.map(note => ({
        ...note,
        tags: note.tags.filter(tagName => {
          const tag = state.tags.find(t => t.name === tagName);
          return tag?.id !== action.payload;
        })
      }));
      return { 
        ...state, 
        tags: filteredTags,
        notes: updatedNotes
      };
    }
    
    case 'SET_SEARCH_QUERY':
      return { ...state, searchQuery: action.payload };
    
    case 'UPDATE_SETTINGS':
      return { ...state, settings: { ...state.settings, ...action.payload } };
    
    case 'RESET_STATE':
      return initialState;
    
    case 'IMPORT_DATA':
      return { 
        ...state, 
        notes: action.payload.notes,
        folders: action.payload.folders
      };
    
    case 'SET_CURRENT_USER':
      return { ...state, currentUser: action.payload };
    
    default:
      return state;
  }
};

// Context接口
interface AppContextType {
  state: AppState;
  // 笔记操作
  addNote: (note: Omit<Note, 'id' | 'createdAt' | 'updatedAt'>) => Note;
  updateNote: (note: Note) => void;
  deleteNote: (id: string) => void;
  moveNoteToFolder: (noteId: string, folderId: string | null) => void;
  setCurrentNote: (note: Note | null) => void;
  // 文件夹操作
  addFolder: (folder: Omit<Folder, 'id' | 'createdAt' | 'updatedAt'>) => void;
  updateFolder: (folder: Folder) => void;
  deleteFolder: (id: string) => void;
  setCurrentFolder: (id: string | null) => void;
  // 标签操作
  addTag: (tag: Omit<Tag, 'id'>) => void;
  updateTag: (tag: Tag) => void;
  deleteTag: (id: string) => void;
  // 搜索操作
  setSearchQuery: (query: string) => void;
  // 设置操作
  updateSettings: (settings: Partial<AppSettings>) => void;
  // 数据操作
  loadData: () => Promise<void>;
  saveData: () => Promise<void>;
  importData: (data: { notes: Note[]; folders: Folder[] }) => void;
  exportData: () => { notes: Note[]; folders: Folder[] };
  resetData: () => void;
}

// 创建Context
const AppContext = createContext<AppContextType | undefined>(undefined);

// Provider组件
interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);
  const didLoadRef = useRef(false);

  const loadData = useCallback(async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const data = realStorageService.loadAllData();
      dispatch({ type: 'SET_NOTES', payload: data.notes });
      dispatch({ type: 'SET_FOLDERS', payload: data.folders });
      dispatch({ type: 'SET_TAGS', payload: data.tags });
      dispatch({ type: 'UPDATE_SETTINGS', payload: data.settings });
      dispatch({ type: 'SET_ERROR', payload: null });
      console.log('数据加载成功:', {
        笔记数量: data.notes.length,
        文件夹数量: data.folders.length,
        标签数量: data.tags.length
      });
    } catch (error) {
      console.error('加载数据失败:', error);
      dispatch({ type: 'SET_ERROR', payload: '加载数据失败，请检查浏览器存储权限' });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []);

  const saveData = useCallback(async () => {
    try {
      const success = realStorageService.saveAllData({
        notes: state.notes,
        folders: state.folders,
        tags: state.tags,
        settings: state.settings
      });
      if (!success) {
        throw new Error('保存操作失败');
      }
    } catch (error) {
      console.error('后台保存数据失败:', error);
      // 在自动保存中不向上抛出或dispatch UI错误，避免循环
    }
  }, [state.notes, state.folders, state.tags, state.settings]);

  // 初始加载，防止严格模式下重复执行
  useEffect(() => {
    if (didLoadRef.current === false) {
      didLoadRef.current = true;
      loadData();
    }
  }, [loadData]);

  // 自动保存，带防抖
  useEffect(() => {
    const handler = setTimeout(() => {
      saveData();
    }, 1500);
    return () => clearTimeout(handler);
  }, [saveData]);

  const addNote = useCallback((note: Omit<Note, 'id' | 'createdAt' | 'updatedAt'>): Note => {
    const newNote: Note = { ...note, id: generateId(), createdAt: new Date(), updatedAt: new Date() };
    dispatch({ type: 'ADD_NOTE', payload: newNote });
    return newNote;
  }, []);

  const updateNote = useCallback((note: Note) => {
    dispatch({ type: 'UPDATE_NOTE', payload: note });
  }, []);

  const deleteNote = useCallback((id: string) => {
    dispatch({ type: 'DELETE_NOTE', payload: id });
  }, []);

  const moveNoteToFolder = useCallback((noteId: string, folderId: string | null) => {
    const note = state.notes.find(n => n.id === noteId);
    if (note) {
      const updatedNote = { ...note, folderId: folderId || 'default', updatedAt: new Date() };
      dispatch({ type: 'UPDATE_NOTE', payload: updatedNote });
    }
  }, [state.notes]);

  const setCurrentNote = useCallback((note: Note | null) => {
    dispatch({ type: 'SET_CURRENT_NOTE', payload: note });
  }, []);

  const addFolder = useCallback((folder: Omit<Folder, 'id' | 'createdAt' | 'updatedAt'>) => {
    dispatch({ type: 'ADD_FOLDER', payload: folder });
  }, []);

  const updateFolder = useCallback((folder: Folder) => {
    dispatch({ type: 'UPDATE_FOLDER', payload: folder });
  }, []);

  const deleteFolder = useCallback((id: string) => {
    dispatch({ type: 'DELETE_FOLDER', payload: id });
  }, []);

  const setCurrentFolder = useCallback((id: string | null) => {
    dispatch({ type: 'SET_CURRENT_FOLDER', payload: id });
  }, []);

  const addTag = useCallback((tag: Omit<Tag, 'id'>) => {
    dispatch({ type: 'ADD_TAG', payload: tag });
  }, []);

  const updateTag = useCallback((tag: Tag) => {
    dispatch({ type: 'UPDATE_TAG', payload: tag });
  }, []);

  const deleteTag = useCallback((id: string) => {
    dispatch({ type: 'DELETE_TAG', payload: id });
  }, []);

  const setSearchQuery = useCallback((query: string) => {
    dispatch({ type: 'SET_SEARCH_QUERY', payload: query });
  }, []);

  const updateSettings = useCallback((settings: Partial<AppSettings>) => {
    dispatch({ type: 'UPDATE_SETTINGS', payload: settings });
  }, []);

  const importData = useCallback((data: { notes: Note[]; folders: Folder[] }) => {
    dispatch({ type: 'IMPORT_DATA', payload: data });
  }, []);

  const exportData = useCallback(() => ({
    notes: state.notes,
    folders: state.folders
  }), [state.notes, state.folders]);

  const resetData = useCallback(() => {
    dispatch({ type: 'RESET_STATE' });
  }, []);

  const contextValue = useMemo(() => ({
    state, addNote, updateNote, deleteNote, moveNoteToFolder, setCurrentNote, addFolder, updateFolder, deleteFolder, setCurrentFolder, addTag, updateTag, deleteTag, setSearchQuery, updateSettings, loadData, saveData, importData, exportData, resetData
  }), [state, addNote, updateNote, deleteNote, moveNoteToFolder, setCurrentNote, addFolder, updateFolder, deleteFolder, setCurrentFolder, addTag, updateTag, deleteTag, setSearchQuery, updateSettings, loadData, saveData, importData, exportData, resetData]);

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

// Hook
export const useApp = (): AppContextType => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};